/**
 * DALVYN Wishlist
 * Lightweight guest wishlist. Single source of truth stored in localStorage so
 * every heart across the storefront stays synchronised.
 *
 * Storage: Array of { id, handle, title, url, variantId }
 *   id        - product id (string)
 *   handle    - product handle (used to refresh data on /pages/wishlist)
 *   title     - snapshot for instant rendering
 *   url       - product url
 *   variantId - pinned variant (selected on the product page), optional
 */
(function () {
  "use strict";

  if (window.dalvynWishlistLoaded) return;
  window.dalvynWishlistLoaded = true;

  var KEY = "dalvyn_wishlist_v1";
  var state = [];
  var page = null; // wishlist page context (set when on /pages/wishlist)
  var toastTimer = null;

  function load() {
    try {
      var raw = window.localStorage.getItem(KEY);
      state = raw ? JSON.parse(raw) : [];
    } catch (e) {
      state = [];
    }
    if (!Array.isArray(state)) state = [];
  }

  function persist() {
    try {
      window.localStorage.setItem(KEY, JSON.stringify(state));
    } catch (e) {
      /* storage unavailable - keep in-memory state */
    }
  }

  function emit() {
    document.dispatchEvent(
      new CustomEvent("wishlist:updated", {
        detail: { items: state.slice() },
        bubbles: true,
      }),
    );
  }

  function find(id) {
    for (var i = 0; i < state.length; i++) {
      if (String(state[i].id) === String(id)) return state[i];
    }
    return null;
  }

  function count() {
    return state.length;
  }

  function getRoot() {
    var root = (window.theme && theme.routes && theme.routes.home) || "/";
    return String(root).replace(/\/$/, "");
  }

  function formatMoney(cents, fallback) {
    try {
      if (window.theme && theme.Currency && cents != null) {
        return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
      }
    } catch (e) {}
    return fallback || "";
  }

  function resizeCdn(url, width) {
    if (!url) return "";
    var base = String(url).replace(/([?&])width=\d+/i, "");
    return base.indexOf("?") > -1
      ? base + "&width=" + width
      : base + "?width=" + width;
  }

  /* product.js returns featured_image as a string; variants as {src}/{url}. */
  function imageSrc(media) {
    if (!media) return "";
    if (typeof media === "string") return media;
    return media.src || media.url || "";
  }

  function fetchProductData(handle) {
    return fetch(
      getRoot() + "/products/" + encodeURIComponent(handle) + ".js",
    ).then(function (res) {
      return res.ok ? res.json() : Promise.reject(new Error("not_found"));
    });
  }

  /* Same string the PDP uses when the selected variant is sold out. */
  function soldOutText(mini) {
    return (
      (window.theme && theme.strings && theme.strings.soldOut) ||
      (mini && mini.soldOut) ||
      "Sold Out"
    );
  }

  /* ---------- Hearts ---------- */

  /* Lightweight snapshot enrich so the wishlist page can render instantly. */
  function enrich(entry) {
    if (!entry.handle) return;
    fetchProductData(entry.handle)
      .then(function (product) {
        var src = imageSrc(product.featured_image);
        if (src) entry.image = resizeCdn(src, 680);
        if (product.price != null) entry.price = product.price;
        if (product.title && !entry.title) entry.title = product.title;
        if (product.url && !entry.url) entry.url = product.url;
        persist();
        emit();
      })
      .catch(function () {});
  }

  function syncHearts() {
    var hearts = document.querySelectorAll(".js-wishlist-heart");
    for (var i = 0; i < hearts.length; i++) {
      var heart = hearts[i];
      var id = heart.getAttribute("data-product-id");
      var saved = !!id && find(id);
      heart.setAttribute("aria-pressed", saved ? "true" : "false");
      var title = heart.getAttribute("data-product-title") || "this product";
      heart.setAttribute(
        "aria-label",
        saved
          ? "Remove " + title + " from wishlist"
          : "Add " + title + " to wishlist",
      );
    }
    syncHeaderDot();
  }

  /* Header/drawer status dot: shown while the wishlist holds 1+ products.
     Toggled from syncHearts() so every state change path updates it. */
  function syncHeaderDot() {
    var dots = document.querySelectorAll(".js-wishlist-bubble");
    var on = count() > 0;
    for (var i = 0; i < dots.length; i++) {
      dots[i].classList.toggle("wishlist-link__bubble--visible", on);
    }
  }

  function toggleHeart(heart) {
    var id = heart.getAttribute("data-product-id");
    var handle = heart.getAttribute("data-product-handle");
    var title = heart.getAttribute("data-product-title") || "";
    var url = heart.getAttribute("data-product-url") || "";

    if (!id) return;

    var existing = find(id);
    var removing = !!existing;
    if (existing) {
      state = state.filter(function (it) {
        return String(it.id) !== String(id);
      });
    } else {
      var variantId = heart.getAttribute("data-variant-id") || null;
      var entry = {
        id: id,
        handle: handle,
        title: title,
        url: url,
        variantId: variantId,
        image: "",
        price: null,
      };
      state.push(entry);
      enrich(entry);
    }

    persist();
    emit();
    syncHearts();
    updatePage();
    popHeart(heart);
    showToast(removing ? "Removed from wishlist" : "Saved to wishlist");
  }

  /* ---------- Wishlist page ---------- */

  function updateCount() {
    if (!page) return;
    var el = page.querySelector("[data-wishlist-count]");
    if (!el) return;
    var n = count();
    var single = page.getAttribute("data-label-single") || "item";
    var plural = page.getAttribute("data-label-plural") || "items";
    el.textContent = n + " " + (n === 1 ? single : plural);
  }

  function showHeaders(withActions) {
    if (!page) return;
    var actions = page.querySelector(".wishlist__actions");
    if (actions) actions.classList.toggle("is-hidden", !withActions);
  }

  function updatePage() {
    if (!page) return;
    var emptyEl = page.querySelector(".js-wishlist-empty");
    var grid = page.querySelector(".wishlist__grid");
    var hasCards = !!grid && !!grid.querySelector(".js-wishlist-card");
    var emptyNow = count() === 0 && !hasCards;
    if (emptyEl) emptyEl.classList.toggle("is-visible", emptyNow);
    if (grid) grid.classList.toggle("is-hidden", emptyNow);
    showHeaders(!emptyNow);
    updateCount();
  }

  function getTemplate() {
    var id = page.getAttribute("data-template-id");
    var tpl = id ? document.getElementById(id) : null;
    return tpl || document.getElementById("WishlistCardTemplate");
  }

  function cardHeartData(card, item) {
    var heart = card.querySelector(".js-wishlist-heart");
    if (!heart) return;
    heart.setAttribute("data-product-id", item.id);
    heart.setAttribute("data-product-handle", item.handle || "");
    heart.setAttribute("data-product-title", item.title || "");
    heart.setAttribute("data-product-url", item.url || "");
    heart.setAttribute("data-variant-id", item.variantId || "");
  }

  function setCardBusy(card, on) {
    var btn = card.querySelector(".js-wishlist-add");
    if (!btn) return;
    if (on) {
      btn.classList.add("btn--loading");
      btn.setAttribute("aria-busy", "true");
      btn.disabled = true;
    } else {
      btn.classList.remove("btn--loading");
      btn.removeAttribute("aria-busy");
      btn.disabled = false;
      var soldOut = !!card.getAttribute("data-sold-out");
      btn.classList.toggle("disabled", soldOut);
      if (soldOut) btn.setAttribute("aria-disabled", "true");
      else btn.removeAttribute("aria-disabled");
    }
  }

  /* Brief, neutral toast (save/remove feedback + sold-out note). Uses the
     wishlist page's element when present, else creates one on <body>. */
  function getToast() {
    var toast =
      (page && page.querySelector(".js-wishlist-toast")) ||
      document.querySelector(".js-wishlist-toast--global");
    if (!toast) {
      toast = document.createElement("div");
      toast.className = "wishlist__toast js-wishlist-toast--global";
      toast.setAttribute("role", "status");
      toast.setAttribute("aria-live", "polite");
      document.body.appendChild(toast);
    }
    return toast;
  }

  function showToast(text) {
    if (!text) return;
    var toast = getToast();
    toast.textContent = text;
    toast.classList.add("is-visible");
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(function () {
      toast.classList.remove("is-visible");
    }, 2600);
  }

  /* Brief, neutral note shown when a sold-out CTA is clicked. */
  function showSoldOutNote() {
    if (!page) return;
    var note =
      page.getAttribute("data-label-soldout-note") ||
      "Sold out for now — still saved in your wishlist.";
    showToast(note);
  }

  /* Subtle pop on toggle (~200ms). Transform only — no layout shift. */
  function popHeart(heart) {
    if (!heart) return;
    heart.classList.remove("wishlist-heart--pop");
    void heart.offsetWidth; /* restart the animation */
    heart.classList.add("wishlist-heart--pop");
    window.setTimeout(function () {
      heart.classList.remove("wishlist-heart--pop");
    }, 220);
  }

  function setCardMessage(card, text, success) {
    var btn = card.querySelector(".js-wishlist-add");
    if (!btn) return;
    var original = btn.getAttribute("data-label-add") || "Add to Cart";
    btn.classList.toggle("wishlist-card__add--success", !!success);
    btn.textContent = text || original;
    window.clearTimeout(btn._wlReset);
    btn._wlReset = window.setTimeout(function () {
      btn.classList.remove("wishlist-card__add--success");
      btn.textContent = original;
    }, 1400);
  }

  function resolveVariant(product, item) {
    var variants = product.variants || [];
    if (item.variantId) {
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(item.variantId)) {
          return { variant: variants[i], needsChoice: false };
        }
      }
      return { variant: null, needsChoice: true }; // pinned variant no longer exists
    }
    if (product.has_only_default_variant || variants.length === 1) {
      var v =
        product.selected_or_first_available_variant ||
        (variants.length ? variants[0] : null);
      return { variant: v, needsChoice: false };
    }
    return { variant: null, needsChoice: true }; // size / colour must be chosen
  }

  function renderCard(item) {
    var tpl = getTemplate();
    if (!tpl) return null;
    var node = tpl.content.cloneNode(true);
    var card = node.querySelector(".js-wishlist-card");
    if (!card) return null;

    card.setAttribute("data-wishlist-id", item.id);
    card.setAttribute("data-product-handle", item.handle || "");
    card.setAttribute("data-product-title", item.title || "");
    card.setAttribute("data-product-url", item.url || "");

    /* Heart (reuse the shared component). */
    cardHeartData(card, item);

    var img = card.querySelector(".wishlist-card__image");
    var imageLink = card.querySelector(".wishlist-card__image-link");
    var titleLink = card.querySelector(".wishlist-card__title");
    var priceEl = card.querySelector(".wishlist-card__price");
    var metaEl = card.querySelector(".wishlist-card__meta");
    var stockEl = card.querySelector(".wishlist-card__stock");
    var addBtn = card.querySelector(".js-wishlist-add");

    if (imageLink) imageLink.href = item.url || "#";
    if (titleLink) {
      titleLink.href = item.url || "#";
      titleLink.textContent = item.title || "";
    }

    var labels = {
      add: (page && page.getAttribute("data-label-add")) || "Add to Cart",
      soldOut: (page && page.getAttribute("data-label-sold-out")) || "Sold out",
      stockIn: (page && page.getAttribute("data-label-in-stock")) || "In stock",
      stockOut:
        (page && page.getAttribute("data-label-out-stock")) || "Sold out",
    };

    if (addBtn) {
      addBtn.textContent = labels.add;
      addBtn.setAttribute("data-label-add", labels.add);
    }

    /* Instant render from the stored snapshot. */
    if (img && item.image) {
      img.src = resizeCdn(item.image, 680);
      img.alt = item.title || "";
    }
    if (priceEl && item.price != null) {
      priceEl.textContent = formatMoney(item.price, "");
    }

    /* Refresh data so prices / availability / variant info are current. */
    fetchProductData(item.handle)
      .then(function (product) {
        updateCard(card, product, item, labels);
      })
      .catch(function () {
        /* keep the snapshot */
      });

    card._wlReset = null;
    return card;
  }

  function updateCard(card, product, item, labels) {
    var resolved = resolveVariant(product, item);
    var variant = resolved.variant;
    var needsChoice = resolved.needsChoice;
    var mini = labels || {};

    var img = card.querySelector(".wishlist-card__image");
    var secondaryWrap = card.querySelector(".wishlist-card__secondary-image");
    var secondaryImg = card.querySelector(".wishlist-card__secondary");
    var priceEl = card.querySelector(".wishlist-card__price");
    var metaEl = card.querySelector(".wishlist-card__meta");
    var stockEl = card.querySelector(".wishlist-card__stock");
    var addBtn = card.querySelector(".js-wishlist-add");

    var available = variant ? !!variant.available : false;
    var productVariants = product.variants || [];
    var soldOutProduct = !(product.available != null
      ? !!product.available
      : productVariants.some(function (v) {
          return !!v.available;
        }));

    if (img) {
      var media =
        variant && imageSrc(variant.featured_image)
          ? variant.featured_image
          : product.featured_image;
      var src = imageSrc(media);
      if (src) {
        img.src = resizeCdn(src, 680);
        img.alt =
          (product.title || "") +
          (variant && variant.title ? " - " + variant.title : "");
      }
    }

    /* Hover swap — same rule as collection cards: hover image enabled + product has 2+ images. */
    var hoverEnabled =
      !!page && page.getAttribute("data-hover-image") === "true";
    var images = product.images || [];
    if (hoverEnabled && images.length > 1 && secondaryWrap && secondaryImg) {
      var secondSrc = imageSrc(images[1]);
      if (secondSrc) {
        secondaryImg.src = resizeCdn(secondSrc, 680);
        secondaryWrap.removeAttribute("hidden");
      }
    }

    if (priceEl) {
      var price = variant ? variant.price : product.price;
      var compare =
        variant && variant.compare_at_price > price
          ? variant.compare_at_price
          : null;
      if (price != null) {
        var markup = "";
        if (compare) {
          markup =
            '<span class="wishlist-card__price--original">' +
            formatMoney(compare, "") +
            "</span>";
        }
        markup += formatMoney(price, "");
        priceEl.innerHTML = markup;
      }
    }

    if (metaEl) {
      var opts = variant && variant.options ? variant.options : [];
      var meaningful = opts.filter(function (o) {
        return !/^(default|title)$/i.test(String(o).trim());
      });
      metaEl.textContent = meaningful.length ? meaningful.join(" / ") : "";
    }

    if (stockEl) {
      if (variant) {
        stockEl.textContent = available
          ? mini.stockIn || "In stock"
          : mini.stockOut || "Sold out";
      } else if (soldOutProduct) {
        stockEl.textContent = mini.stockOut || "Sold out";
      }
    }

    var badge = card.querySelector(
      ".wishlist-card__media .grid-product__tag--sold-out",
    );
    if (badge) {
      if (soldOutProduct) badge.removeAttribute("hidden");
      else badge.setAttribute("hidden", "");
    }

    if (addBtn) {
      var ctaSoldOut = soldOutProduct || (!needsChoice && !available);
      var addLabel = mini.add || "Add to Bag";
      card.setAttribute("data-variant-id", variant ? variant.id : "");
      card.setAttribute("data-sold-out", ctaSoldOut ? "true" : "");
      card.setAttribute("data-needs-choice", needsChoice ? "true" : "");
      addBtn.disabled = false;
      addBtn.classList.toggle("disabled", ctaSoldOut);
      if (ctaSoldOut) addBtn.setAttribute("aria-disabled", "true");
      else addBtn.removeAttribute("aria-disabled");
      addBtn.textContent = ctaSoldOut ? soldOutText(mini) : addLabel;
      addBtn.setAttribute("data-label-add", addLabel);
      addBtn.classList.remove("btn--loading");
      addBtn.removeAttribute("aria-busy");
      ensureQuickShop(card);
    }

    /* Cache so later modal selections can refresh this card. */
    card._wlProduct = product;
    card._wlLabels = mini;
  }

  function refreshCardAvailability(card) {
    var now = Date.now();
    if (card._wlAvailNext && now < card._wlAvailNext) return;
    card._wlAvailNext = now + 30000;
    var handle = card.getAttribute("data-product-handle");
    var entry = find(card.getAttribute("data-wishlist-id"));
    if (!handle || !entry || !card._wlProduct) return;
    fetchProductData(handle)
      .then(function (product) {
        updateCard(card, product, entry, card._wlLabels);
      })
      .catch(function () {});
  }

  /* ---------- Quick Add (reuses the theme's existing quick shop) ---------- */

  function quickShopAvailable() {
    return !!(
      window.theme &&
      theme.settings &&
      theme.settings.quickView &&
      typeof theme.preloadProductModal === "function" &&
      typeof theme.Modals === "function"
    );
  }

  /* theme.preloadProductModal() fills the holder and constructs theme.Modals
     synchronously in one .then — a populated holder means the shared open
     trigger (.js-modal-open-quick-modal-{id}) is already bound. */
  function quickShopReady(handle) {
    var holder = handle && document.getElementById("QuickShopHolder-" + handle);
    return !!(holder && holder.children.length);
  }

  /* Clone the quick-shop-modal shell into the card and register the same
     trigger class the theme binds. Runs once per card. */
  function ensureQuickShop(card) {
    if (card._wlQuickShopInit) return true;
    if (!quickShopAvailable()) return false;
    var id = card.getAttribute("data-wishlist-id");
    var handle = card.getAttribute("data-product-handle");
    if (!id || !handle) return false;

    if (!document.getElementById("QuickShopModal-" + id)) {
      var tpl = document.getElementById("WishlistQuickShopModalTemplate");
      if (!tpl) return false;
      var shell = tpl.content.firstElementChild.cloneNode(true);
      var holder = shell && shell.querySelector('[id="QuickShopHolder-"]');
      if (!shell || !holder) return false;
      shell.id = "QuickShopModal-" + id;
      shell.setAttribute("data-product-id", id);
      holder.id = "QuickShopHolder-" + handle;
      card.appendChild(shell);
    }

    var btn = card.querySelector(".quick-product__btn");
    if (btn) btn.classList.add("js-modal-open-quick-modal-" + id);
    card._wlQuickShopInit = true;
    return true;
  }

  /* Same lazy preload as collection cards: hover / focus fetches the
     shared ?view=modal content and wires up theme.Modals. */
  function preloadQuickShop(card) {
    if (!quickShopAvailable() || !ensureQuickShop(card)) return;
    var id = card.getAttribute("data-wishlist-id");
    var handle = card.getAttribute("data-product-handle");
    if (card._wlQuickLoading || quickShopReady(handle)) return;
    card._wlQuickLoading = true;
    theme.preloadProductModal(
      handle,
      id,
      card.querySelector(".quick-product__btn"),
    );
  }

  /* Open the existing Quick Add modal in place. Add to Bag is the single
     entry point on wishlist cards: variant selection and cart logic then
     run exactly as on regular collection cards. Never redirects to the PDP. */
  function openQuickShop(card) {
    var handle = card.getAttribute("data-product-handle");
    var addBtn = card.querySelector(".js-wishlist-add");

    function fail() {
      if (addBtn) setCardBusy(card, false);
      setCardMessage(
        card,
        (page && page.getAttribute("data-label-error")) || "Try again",
        false,
      );
    }

    if (!quickShopAvailable() || !ensureQuickShop(card)) {
      fail();
      return;
    }

    var btn = card.querySelector(".quick-product__btn");

    if (quickShopReady(handle)) {
      /* Fires the theme's own .js-modal-open-quick-modal-{id} binding. */
      if (btn) btn.click();
      return;
    }

    preloadQuickShop(card);

    var tries = 0;
    if (addBtn) setCardBusy(card, true);
    (function waitReady() {
      if (quickShopReady(handle)) {
        if (addBtn) setCardBusy(card, false);
        if (btn) btn.click();
        return;
      }
      if (++tries > 60) {
        fail();
        return;
      }
      window.setTimeout(waitReady, 100);
    })();
  }

  function addCardToCart(card) {
    var btn = card.querySelector(".js-wishlist-add");
    if (!btn || btn.disabled || btn.classList.contains("btn--loading")) return;
    if (card.getAttribute("data-sold-out") === "true") {
      showSoldOutNote();
      return;
    }
    if (!card._wlProduct) return;
    openQuickShop(card);
  }

  /* Keep a card's cart-variant state in sync with a selection made inside
     its Quick Add modal. Wishlist membership is never touched here: the
     wishlist is keyed by product.id only; variantId is a cart preference. */
  function recordSelection(card, variant) {
    var entry = find(card.getAttribute("data-wishlist-id"));
    if (entry) {
      entry.variantId = String(variant.id);
      persist();
      if (card._wlProduct) {
        updateCard(card, card._wlProduct, entry, card._wlLabels);
        return;
      }
    }
    card.setAttribute("data-variant-id", String(variant.id));
    card.setAttribute("data-needs-choice", "");
    card.setAttribute("data-sold-out", variant.available ? "" : "true");
  }

  /* ---------- Clear confirmation ---------- */

  var confirmLastFocus = null;

  function getClearConfirm() {
    return page ? page.querySelector(".js-wishlist-confirm") : null;
  }

  function openClearConfirm() {
    var dlg = getClearConfirm();
    if (!dlg || !count()) return;
    confirmLastFocus = document.activeElement;
    dlg.classList.add("is-visible");
    var cancel = dlg.querySelector(".wishlist-confirm__cancel");
    if (cancel) cancel.focus();
  }

  function closeClearConfirm() {
    var dlg = getClearConfirm();
    if (!dlg || !dlg.classList.contains("is-visible")) return;
    dlg.classList.remove("is-visible");
    var focus = confirmLastFocus;
    confirmLastFocus = null;
    if (focus && focus.focus) focus.focus();
  }

  /* Remove every product from the wishlist at once. Cards fade out first
     (existing .is-removing transition), then the empty state is revealed. */
  function clearWishlist() {
    if (!page || !count()) return;

    /* Close any open quick-add modal so the theme releases the body lock. */
    var active = page.querySelector(".modal--is-active");
    if (active) {
      var closeBtn = active.querySelector(".js-modal-close");
      if (closeBtn) closeBtn.click();
    }

    var grid = page.querySelector(".wishlist__grid");
    var cards = grid ? grid.querySelectorAll(".wishlist-card") : [];
    for (var i = 0; i < cards.length; i++) {
      cards[i].classList.add("is-removing");
    }

    state = [];
    persist();
    emit();
    syncHearts();
    window.setTimeout(function () {
      renderGrid(); /* grid empties → updatePage reveals the empty state */
    }, 240);
  }

  /* (Re)build the wishlist grid from the shared state. Also used when
     another tab writes to localStorage. */
  function renderGrid() {
    if (!page) return;
    var grid = page.querySelector(".wishlist__grid");
    var tpl = getTemplate();
    if (!grid || !tpl) return;

    var frag = document.createDocumentFragment();
    var items = state.slice();
    for (var i = 0; i < items.length; i++) {
      var card = renderCard(items[i]);
      if (card) frag.appendChild(card);
    }
    grid.textContent = "";
    grid.appendChild(frag);
    syncHearts();
    updatePage();
  }

  function initPage() {
    var el = document.querySelector(".js-wishlist-page");
    if (!el) return;
    page = el;

    load();
    syncHearts();

    var grid = page.querySelector(".wishlist__grid");
    var tpl = getTemplate();
    if (!grid || !tpl) return;

    renderGrid();
    el.classList.add("wishlist-page--ready");

    grid.addEventListener("click", function (evt) {
      var add = evt.target.closest(".js-wishlist-add");
      if (add) {
        evt.preventDefault();
        var card = add.closest(".js-wishlist-card");
        if (card) addCardToCart(card);
        return;
      }
      var heart = evt.target.closest(".js-wishlist-heart");
      if (heart) {
        evt.preventDefault();
        evt.stopPropagation();
        toggleHeart(heart);
        var removeCard = heart.closest(".js-wishlist-card");
        if (removeCard) animateRemove(removeCard);
      }
    });

    /* Preload the shared quick shop content on hover / focus so clicking
       Add to Bag opens the modal instantly (same lazy load as collections). */
    function quickShopHover(evt) {
      if (window.theme && theme.config && theme.config.bpSmall) return;
      var card = evt.target.closest(".js-wishlist-card");
      if (card) {
        preloadQuickShop(card);
        refreshCardAvailability(card);
      }
    }
    grid.addEventListener("mouseover", quickShopHover);
    grid.addEventListener("focusin", quickShopHover);

    var clearBtn = page.querySelector(".js-wishlist-clear");
    var confirmDlg = page.querySelector(".js-wishlist-confirm");
    if (clearBtn) {
      clearBtn.addEventListener(
        "click",
        confirmDlg ? openClearConfirm : clearWishlist,
      );
    }
    if (confirmDlg) {
      confirmDlg.addEventListener("click", function (evt) {
        if (evt.target.closest("[data-wishlist-confirm-close]")) {
          closeClearConfirm();
          return;
        }
        if (evt.target.closest(".js-wishlist-confirm-clear")) {
          closeClearConfirm();
          clearWishlist();
        }
      });
      document.addEventListener("keydown", function (evt) {
        if (evt.key === "Escape") closeClearConfirm();
      });
    }
  }

  function animateRemove(card) {
    /* Close this card's quick shop modal (if open) so the theme can
       release the body scroll lock before the shell is detached. */
    var active = card.querySelector(".modal--is-active");
    if (active) {
      var closeBtn = active.querySelector(".js-modal-close");
      if (closeBtn) closeBtn.click();
    }

    card.classList.add("is-removing");

    function finish() {
      if (card.parentNode) card.parentNode.removeChild(card);
      updatePage();
    }

    var done = false;
    function once() {
      if (done) return;
      done = true;
      finish();
    }
    card.addEventListener("transitionend", once);
    window.setTimeout(once, 260);
  }

  /* ---------- Global wiring ---------- */

  function onReady() {
    load();
    syncHearts();
    initPage();

    /* Toggle from any heart on the storefront (collections, featured, product). */
    document.addEventListener("click", function (evt) {
      var heart = evt.target.closest(".js-wishlist-heart");
      if (!heart) return;
      if (heart.closest(".js-wishlist-grid")) return; // handled by grid listener
      evt.preventDefault();
      evt.stopPropagation();
      toggleHeart(heart);
    });

    /* Keep the product page heart on the currently selected variant. */
    document.addEventListener("variant:change", function (evt) {
      var detail = evt.detail || {};
      var variant = detail.variant;
      if (!variant) {
        if (detail.data && detail.data.variant) variant = detail.data.variant;
      }
      if (!variant) return;

      var hearts = document.querySelectorAll(
        ".js-wishlist-heart.wishlist-heart--inline",
      );
      for (var i = 0; i < hearts.length; i++) {
        hearts[i].setAttribute("data-variant-id", String(variant.id));
        var id = hearts[i].getAttribute("data-product-id");
        if (id) {
          var entry = find(id);
          if (entry) {
            entry.variantId = String(variant.id);
            persist();
          }
        }
      }
    });

    /* Any other page rendering hearts after new content arrives. */
    document.addEventListener("wishlist:updated", syncHearts);

    /* Selections made inside a wishlist card's Quick Add modal.
       `variantChange` is dispatched on the product container (inside the
       modal, inside the card); captured here before other handlers run. */
    document.addEventListener(
      "variantChange",
      function (evt) {
        if (!page) return;
        var target = evt.target;
        if (!target || !target.closest) return;
        var card = target.closest(".js-wishlist-card");
        if (!card) return;
        var variant = evt.detail && evt.detail.variant;
        if (!variant || !variant.id) return;
        recordSelection(card, variant);
      },
      true,
    );

    /* The customer added the item from inside its Quick Add modal: pin the
       variant that actually went into the bag so the card resolves next time. */
    document.addEventListener(
      "ajaxProduct:added",
      function (evt) {
        if (!page) return;
        var src = evt.detail && evt.detail.addToCartBtn;
        var host = null;
        if (src && src.closest) host = src.closest(".js-wishlist-card");
        if (!host && evt.target && evt.target.closest) {
          host = evt.target.closest(".js-wishlist-card");
        }
        if (!host) return;

        var sel = host.querySelector("[data-product-select]");
        var variantId = sel ? sel.value : "";
        if (variantId)
          recordSelection(host, { id: variantId, available: true });
      },
      true,
    );

    /* Cross-tab: localStorage stays the single source of truth. */
    window.addEventListener("storage", function (evt) {
      if (evt.key && evt.key !== KEY) return;
      load();
      syncHearts();
      renderGrid();
    });

    /* Hearts injected after page load (product recommendations, quick-shop
       modal content, editor sections) must sync to the shared state too. */
    if (window.MutationObserver && document.body) {
      var heartSyncScheduled = false;
      var observer = new MutationObserver(function (mutations) {
        if (heartSyncScheduled) return;
        for (var m = 0; m < mutations.length; m++) {
          if (mutations[m].addedNodes && mutations[m].addedNodes.length) {
            heartSyncScheduled = true;
            window.requestAnimationFrame(function () {
              heartSyncScheduled = false;
              syncHearts();
            });
            return;
          }
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", onReady);
  } else {
    onReady();
  }
})();
